import type { ListBuilderList } from '../../../types';
import {
	accountColumn,
	boardActionColumn,
	flagOnlyActionColumn,
	licenseColumn,
	salesColumn,
	whyHereColumn,
} from '../columns';
import { hideOtherCardFilters } from '../filter-rules';
import { COMPLIANCE_ATTENTION_LIST_IDS } from '../list-ids';

/**
 * Adverse Action — FLAG ONLY (PD-16615). The same population as the Adverse
 * list (active buyers, a board-flagged license still in force), for the board
 * records where Workd holds the flag and no detail yet: no type, date, case,
 * order or document on file (VA "Additional Public Information = Yes", a TX
 * bulk `DISP ACTN = Y` whose order PDFs are not parsed yet, an NJ "YES*" with
 * no documents). What is missing is OUR capture, not necessarily the board's
 * publication, and the row copy says so.
 *
 * PD-16464 hid these (2026-09-24). Chris, 2026-09-27: "the adverse actions
 * need to show up and then we can just have the compliance officer for the
 * company look it up and determine what is going on, then they can mark it
 * off however they want." So they are back, as their OWN group rather than
 * mixed into the list of described actions: each row offers the board's
 * lookup link and the SAME review, with the same outcomes and the same
 * mark-off rule (hidden until the board record changes or the follow-up date
 * arrives). An item the board later substantiates moves to the Adverse list
 * by itself.
 *
 * COUNT PARITY. The locked `licenseAdverseInForce: flag_only` is the same
 * in-force fragment narrowed to the licenses service's flag-only set
 * (`isSubstantiatedBoardAction` false, no binding review), which the dashboard
 * card nets with the same two verdicts — its "flag only" figure is this list's
 * row count.
 */
export const adverseFlagOnlyList: ListBuilderList = {
	id: COMPLIANCE_ATTENTION_LIST_IDS.adverseFlagOnly,
	cacheTTL: 300000,
	name: 'Flag Only',
	type: 'default',
	active: true,
	// Directly after Adverse: the card's second figure opens here.
	sortOrder: 3,
	filterRules: {
		...hideOtherCardFilters(['licenseAdverseInForce']),
		licenseActiveBuyer: {
			defaultValue: [{ id: 'yes', label: 'Buying (sale in 90 days)' }],
			locked: true,
		},
		licenseAdverseInForce: {
			defaultValue: [
				{ id: 'flag_only', label: 'Board flag only, license still in force' },
			],
			locked: true,
		},
	},
	// No Action Dates column: a flag-only record has no dates by definition,
	// so the column would be a stack of dashes on every row.
	columns: [
		accountColumn,
		salesColumn,
		licenseColumn,
		boardActionColumn,
		whyHereColumn,
		flagOnlyActionColumn,
	],
	defaultSortId: 'sales-90-desc',
};
